/**
 * contactSyncCrypto.ts
 *
 * Client-side E2EE cryptography for the contact sync feature.
 * Uses the browser's built-in Web Crypto API — zero external dependencies.
 *
 * Algorithm (mirrors lib/core/services/contacts_crypto_service.dart):
 *   1. X25519 ECDH         → 32-byte raw shared secret
 *   2. HKDF-SHA-256        → 32-byte AES-256 key
 *        salt = UTF-8(deviceId)
 *        info = UTF-8("beesphone-contacts-v1")
 *   3. AES-256-GCM encrypt → nonce(12) || ciphertext || auth-tag(16)
 */

const SCHEME_INFO = new TextEncoder().encode('beesphone-contacts-v1');

// ── Key Generation ────────────────────────────────────────────────────────────

export interface WebKeyPair {
  /** Non-extractable private key — stored directly in IndexedDB via structured clone. */
  privateKey: CryptoKey;
  /** The 32-byte X25519 public key, base64-encoded for embedding in the QR code. */
  publicKeyBase64: string;
}

/**
 * Generates a fresh X25519 keypair.
 * The private key is marked non-extractable so it cannot be read back as bytes
 * from JavaScript — it is only usable via the Crypto API inside this origin.
 */
export async function generateWebKeyPair(): Promise<WebKeyPair> {
  const keyPair = await crypto.subtle.generateKey(
    { name: 'X25519' } as AlgorithmIdentifier,
    // extractable: true is required to export the 32-byte public key for the QR code.
    // The private key's extractability inherits this flag but we never call exportKey
    // on the private key — we rely on structured clone for IndexedDB persistence.
    true,
    ['deriveBits'],
  ) as CryptoKeyPair;

  const pubKeyRaw = new Uint8Array(
    await crypto.subtle.exportKey('raw', keyPair.publicKey),
  );

  return {
    privateKey: keyPair.privateKey,
    publicKeyBase64: bufferToBase64(pubKeyRaw),
  };
}

// ── Shared-Secret Derivation ──────────────────────────────────────────────────

/**
 * Derives a 256-bit AES-GCM key from this session's private key and the
 * mobile's published X25519 public key, using HKDF-SHA-256 with deviceId as
 * the salt. This mirrors the HKDF step in the Flutter crypto service.
 */
export async function deriveAesKey(
  myPrivateKey: CryptoKey,
  mobilePublicKeyBase64: string,
  deviceId: string,
): Promise<CryptoKey> {
  // 1. Import mobile's raw public key for ECDH.
  const mobilePublicKey = await crypto.subtle.importKey(
    'raw',
    base64ToBuffer(mobilePublicKeyBase64),
    { name: 'X25519' } as AlgorithmIdentifier,
    false,
    [],
  );

  // 2. ECDH: raw 32-byte shared secret.
  const sharedBits = await crypto.subtle.deriveBits(
    { name: 'X25519', public: mobilePublicKey } as AlgorithmIdentifier,
    myPrivateKey,
    256,
  );

  // 3. Import shared bits as HKDF key material.
  const hkdfKey = await crypto.subtle.importKey(
    'raw',
    sharedBits,
    'HKDF',
    false,
    ['deriveKey'],
  );

  // 4. HKDF-SHA-256 → AES-256-GCM key.
  //    Salt = UTF-8(deviceId) — binds the derived key to this specific pairing.
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new TextEncoder().encode(deviceId),
      info: SCHEME_INFO,
    } as HkdfParams,
    hkdfKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt'],
  );
}

// ── Decryption ────────────────────────────────────────────────────────────────

export interface EncryptedPayload {
  v: number;
  nonce: string;      // base64(12 bytes)
  ciphertext: string; // base64(encrypted bytes + 16-byte auth tag)
}

/**
 * Decrypts an [EncryptedPayload] produced by the Flutter app.
 * Returns the plaintext JSON string.
 *
 * Throws a DOMException with name "OperationError" if the auth tag is invalid
 * (tampered data or wrong key), which must be caught by the caller.
 */
export async function decryptPayload(
  aesKey: CryptoKey,
  payload: EncryptedPayload,
): Promise<string> {
  if (payload.v !== 1) {
    throw new Error(`Unsupported payload version: ${payload.v}`);
  }

  const nonce = base64ToBuffer(payload.nonce);
  // The ciphertext field contains encrypted bytes + 16-byte AES-GCM auth tag,
  // exactly as expected by SubtleCrypto.decrypt for AES-GCM.
  const ciphertext = base64ToBuffer(payload.ciphertext);

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: nonce },
    aesKey,
    ciphertext,
  );

  return new TextDecoder().decode(plaintext);
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function bufferToBase64(buffer: Uint8Array): string {
  return btoa(String.fromCharCode(...buffer));
}

function base64ToBuffer(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}
